import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaTrashAlt } from 'react-icons/fa';
import axios from '../../utils/axiosInstance';
import { toastSuccess, toastError } from "../../utils/alertHelper";

export default function ClientOffer() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    clientName: '',
    startDate: '',
    endDate: '',
    rows: [
      { itemId: '', originalPrice: '', finalPrice: '' }
    ]
  });

  // Helpers to normalize different user shapes returned by the API
  const getUserId = (u) => {
    if (!u) return '';
    const maybe = (x) => {
      if (!x) return undefined;
      if (typeof x === 'string') return x;
      if (typeof x === 'number') return String(x);
      if (x._id) return x._id;
      if (x.id) return x.id;
      if (x?.user && (x.user._id || x.user.id)) return x.user._id || x.user.id;
      if (x._doc && (x._doc._id || x._doc.id)) return x._doc._id || x._doc.id;
      return undefined;
    };
    return maybe(u) || maybe(u.user) || maybe(u.user?.user) || '';
  };

  const getUserName = (u) => {
    if (!u) return '';
    if (typeof u === 'string') return u;
    const pick = (obj) => {
      if (!obj) return '';
      if (obj.firstName) return `${obj.firstName} ${obj.lastName || ''}`.trim();
      if (obj.name) return obj.name;
      if (obj.fullName) return obj.fullName;
      if (obj.companyName) return obj.companyName;
      if (obj.company?.name) return obj.company.name;
      if (obj.email) return obj.email;
      return '';
    };
    // try common shapes in order
    return pick(u) || pick(u.user) || pick(u.data) || pick(u._doc) || '';
  };

  useEffect(() => {
    async function fetchLists() {
      try {
        const [cRes, iRes] = await Promise.all([
          axios.get(`/api/user`),
          axios.get(`/admin/api/item`)
        ]);

        const extractArray = (res) => {
          const data = res?.data ?? res;
          if (Array.isArray(data)) return data;
          // common nested shapes
          if (Array.isArray(res?.data?.data?.users)) return res.data.data.users;
          if (Array.isArray(res?.data?.users)) return res.data.users;
          if (Array.isArray(data.users)) return data.users;
          if (Array.isArray(data.Items)) return data.Items;
          if (Array.isArray(data.items)) return data.items;
          if (Array.isArray(data.data)) return data.data;
          // pick the first array found in object values (e.g., { totalUsers: 7, users: [...] })
          if (data && typeof data === 'object') {
            const firstArray = Object.values(data).find(v => Array.isArray(v));
            if (Array.isArray(firstArray)) return firstArray;
          }
          return [];
        };

        let clientList = extractArray(cRes);

        // Keep only users that are clients. Support various shapes.
        const isClient = (u) => {
          if (!u) return false;
          const tryVals = [];
          tryVals.push(u.userType || u.type || u.role || '');
          if (u.user && typeof u.user === 'object') tryVals.push(u.user.userType || u.user.type || u.user.role || '');
          if (u._doc && typeof u._doc === 'object') tryVals.push(u._doc.userType || u._doc.type || u._doc.role || '');
          return tryVals.some(v => typeof v === 'string' && v.toLowerCase() === 'client');
        };

        const clientListFiltered = Array.isArray(clientList) ? clientList.filter(isClient) : [];
        let itemList = extractArray(iRes);

        // Only expose items that are available
        const availableItems = Array.isArray(itemList)
          ? itemList.filter((it) => it && (it.isAvailable === true || it.isAvailable === 'true'))
          : [];

        setUsers(clientListFiltered);
        setItems(availableItems);

        // if editing, fetch the existing offer and populate the form
        if (id) {
          try {
            const tryUrls = [`/admin/api/priceClient/${id}`];
            let res;
            for (const u of tryUrls) {
              try { res = await axios.get(u); if (res) break; } catch (e) { }
            }
            if (res) {
              const d = res.data ?? res;
              const payload = d.priceClients || d.priceClient || d.data || d;
              const offer = Array.isArray(payload) ? payload[0] : payload;
              const normalizePriceFromItem = (it) => {
                if (!it) return '';
                if (it.salesPrice) return it.salesPrice;
                if (Array.isArray(it.stateRate) && it.stateRate[0]?.price) return it.stateRate[0].price;
                return it.price || it.rate || it.originalPrice || '';
              };
              const rows = (Array.isArray(offer.items) ? offer.items : []).map(it => {
                const itemId = it?.item?._id || it?.item || it.itemId || it._id || it.id || '';
                const offerPrice = it?.finalPrice ?? it?.offerPrice ?? it?.finalPrice ?? '';
                const sel = itemList.find(itm => itm._id === itemId || itm.item?._id === itemId || itm.id === itemId);
                const originalPrice = sel ? normalizePriceFromItem(sel.item || sel) : (it.originalPrice || it.item?.price || it.price || '');
                return { itemId, originalPrice, finalPrice: offerPrice };
              });

              setForm(prev => ({
                ...prev,
                clientName: getUserId(offer.clientName) || getUserId(offer.client) || offer.clientName || '',
                startDate: offer.startDate ? String(offer.startDate).split('T')[0] : (offer.startDate || ''),
                endDate: offer.endDate ? String(offer.endDate).split('T')[0] : (offer.endDate || ''),
                rows: rows.length ? rows : prev.rows,
              }));
            }
          } catch (e) {
            console.error('Failed to load offer for edit', e);
          }
        }
      } catch (err) {
        console.error(err);
        toastError('Failed to load clients or items');
      }
    }
    fetchLists();
  }, []);

  const handleRowChange = (idx, field, value) => {
    setForm(prev => {
      const rows = [...prev.rows];
      rows[idx] = { ...rows[idx], [field]: value };
      // If itemId changed, try to fill originalPrice from items list
      if (field === 'itemId') {
        const sel = items.find(it => it._id === value || it.item?._id === value);
        const normalizePriceFromItem = (it) => {
          if (!it) return '';
          if (it.salesPrice) return it.salesPrice;
          if (Array.isArray(it.stateRate) && it.stateRate[0]?.price) return it.stateRate[0].price;
          return it.price || it.rate || '';
        };
        rows[idx].originalPrice = sel ? normalizePriceFromItem(sel.item || sel) : '';
      }
      return { ...prev, rows };
    });
  };

  const addRow = () => {
    setForm(prev => ({ ...prev, rows: [...prev.rows, { itemId: '', originalPrice: '', finalPrice: '' }] }));
  };

  const removeRow = (idx) => {
    setForm(prev => ({ ...prev, rows: prev.rows.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const selectedClient = users.find(u => getUserId(u) === form.clientName);
      const payload = {
        clientName: form.clientName,
        startDate: form.startDate,
        endDate: form.endDate,
        items: form.rows.map(r => ({ item: r.itemId, finalPrice: r.finalPrice }))
      };
      if (id) {
        await axios.put(`/admin/api/priceClient/${id}`, payload);
      } else {
        await axios.post('/admin/api/priceClient', payload);
      }
      toastSuccess('Offer saved');
      navigate('/admin/price');
    } catch (err) {
      console.error(err);
      toastError('Failed to save offer');
    }
  };

  return (
    <div className="container-fluid py-4 px-3">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h2 fw-bold">Client Offer</h1>
        <a href="/admin/price" className="text-decoration-none text-secondary">← Back</a>
      </div>

      <form className="rounded-3 p-4" style={{ background: '#F6F7F8', border: '1.5px solid #BBBFD2' }} onSubmit={handleSubmit}>
        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <label className="form-label">Client</label>
            <select className="form-select" value={form.clientName} onChange={e => setForm({ ...form, clientName: e.target.value })} required>
              <option value="">Select Client</option>
              {users.map((c, i) => {
                const id = getUserId(c) || i;
                const name = getUserName(c) || '';
                return <option key={id} value={id}>{name}</option>;
              })}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Start Date</label>
            <input type="date" className="form-control" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} required />
          </div>
          <div className="col-md-3">
            <label className="form-label">End Date</label>
            <input type="date" className="form-control" value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })} required />
          </div>
        </div>

        <table className="table table-bordered">
          <thead>
            <tr>
              <th>Sr.no</th>
              <th>Item</th>
              <th>Original Price</th>
              <th>Offer Price</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {form.rows.map((r, idx) => (
              <tr key={idx}>
                <td>{idx + 1}</td>
                <td>
                  <select className="form-select" value={r.itemId} onChange={e => handleRowChange(idx, 'itemId', e.target.value)} required>
                    <option value="">Select Item</option>
                    {items.map((it, idx) => {
                      const id = it._id || it.item?._id || it.id || idx;
                      const name = it.name || it.item?.name || it.Item?.name || it.title || it.skucode || it.code || '';
                      return <option key={id} value={id}>{name}</option>;
                    })}
                  </select>
                </td>
                <td>
                  <input type="text" className="form-control" value={r.originalPrice} readOnly />
                </td>
                <td>
                  <input type="number" className="form-control" value={r.finalPrice} onChange={e => handleRowChange(idx, 'finalPrice', e.target.value)} required />
                </td>
                <td>
                  <div className="d-flex">
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm p-1 d-flex align-items-center justify-content-center"
                      onClick={() => removeRow(idx)}
                      title="Remove"
                    >
                      <FaTrashAlt size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mb-3">
          <button type="button" className="btn btn-primary" onClick={addRow}>+</button>
        </div>

        <div className="d-flex justify-content-end gap-3">
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin/price')}>Cancel</button>
          <button type="submit" className="btn btn-primary">Save Offer</button>
        </div>
      </form>
    </div>
  );
}